import { getDefaultNavigationItems } from 'components/navigation/navigationConstants';
import { hasPermission } from 'actions/roles';
import { SEARCH_FILTERS } from 'constants/localStorageConstants';
import dayjs from 'utils/customDayJS';
import { t } from 'i18next';

const startOfYear = dayjs().startOf('year').unix(); //for use in reportsModulesList
const currentDay = dayjs().unix(); //for use in reportsModulesList

export const checkNavigationForUserPermission = ({ userRole, mobile = false }) => {
	const userCanViewStream = hasPermission({
		targetVal: 'stream_view',
		userRole: userRole
	});

	const userCanViewUsers = hasPermission({ targetVal: 'user_view', userRole });
	const userCanViewAircraft = hasPermission({ targetVal: 'aircraft_view', userRole });
	const userCanViewFlights = hasPermission({ targetVal: 'flight_view', userRole });
	const userCanViewReports = userCanViewUsers && userCanViewAircraft && userCanViewFlights;
	const canViewSetting = hasPermission({
		targetVal: 'account_setting_view',
		userRole: userRole
	});

	const filteredNavItems = getDefaultNavigationItems().filter(item => {
		if (item.name === 'Stream') {
			if (userCanViewStream) {
				return item;
			}
			return null;
		}
		if (item.name === 'Reports') {
			if (userCanViewReports && !mobile) {
				return item;
			}
			return null;
		}
		if (item.name === 'Account Settings') {
			return canViewSetting ? item : null;
		}
		return mobile && item.name === 'Airspace' ? null : item;
	});
	return filteredNavItems;
};

export const createDefaultManageModulesList = () => {
	const localStorageSearchFilters = localStorage.getItem(SEARCH_FILTERS);

	let savedFilters = {};
	if (localStorageSearchFilters) {
		savedFilters = JSON.parse(localStorageSearchFilters);
	}

	return [
		{
			categoryLabel: t('Operations'),
			items: [
				{
					label: t('Missions'),
					itemLabel: t('mission'),
					value: 'missions',
					params: savedFilters.missions
						? savedFilters.missions
						: 'order_by=starts_at&order=desc&search=&mapView=false',
					permission: 'mission_view'
				}
			]
		},
		{
			categoryLabel: t('Logs'),
			items: [
				{
					label: t('Flights'),
					itemLabel: t('flight'),
					value: 'flights',
					params: savedFilters.flights
						? savedFilters.flights
						: 'order_by=flight_time&order=desc&search=',
					permission: 'flight_view'
				},
				{
					label: t('Authorizations'),
					itemLabel: t('authorization'),
					value: 'authorizations',
					params: savedFilters.authorizations
						? savedFilters.authorizations
						: 'order_by=starts_at&order=desc&search=',
					permission: ''
				},
				{
					label: t('Checklists'),
					itemLabel: t('checklist log'),
					value: 'checklist-logs',
					params: savedFilters['checklist-logs']
						? savedFilters['checklist-logs']
						: 'order_by=completed_at&order=desc',
					permission: 'checklist_execution_view'
				},
				{
					label: t('Risk Assessments'),
					itemLabel: t('risk assessment log'),
					value: 'risk-assessment-logs',
					params: savedFilters['risk-assessment-logs']
						? savedFilters['risk-assessment-logs']
						: 'order_by=completed_at&order=desc',
					permission: 'risk_assessment_execution_view'
				},
				{
					label: t('Maintenance'),
					itemLabel: t('maintenance log'),
					value: 'maintenance',
					params: savedFilters.maintenance
						? savedFilters.maintenance
						: 'order_by=performed_at&order=desc&aircraft_name=',
					permission: 'maintenance_view'
				},
				{
					label: t('Incidents'),
					itemLabel: t('incident'),
					value: 'incidents',
					params: savedFilters.incidents ? savedFilters.incidents : 'order=desc',
					permission: 'incident_view'
				}
			]
		},
		{
			categoryLabel: t('Assets'),
			items: [
				{
					label: t('Aircraft'),
					itemLabel: t('aircraft'),
					value: 'aircraft',
					params: savedFilters.aircraft
						? savedFilters.aircraft
						: 'retired=false&order_by=identifier&order=asc&search=',
					permission: 'aircraft_view'
				},

				{
					label: t('Batteries'),
					itemLabel: t('battery'),
					value: 'batteries',
					params: savedFilters.batteries
						? savedFilters.batteries
						: 'retired=false&order_by=identifier&order=asc&search=',
					permission: 'battery_view'
				}
			]
		},
		{
			categoryLabel: t('Procedures'),
			items: [
				{
					label: t('Workflows'),
					itemLabel: t('workflow'),
					value: 'workflows',
					params: savedFilters.workflows ? savedFilters.workflows : 'exclude_retired=true&search=',
					permission: 'workflow_view'
				},
				{
					label: t('Checklists'),
					itemLabel: t('checklist'),
					value: 'checklists',
					params: savedFilters.checklists
						? savedFilters.checklists
						: 'exclude_retired=true&order_by=title&order=asc&search=',
					permission: 'checklist_view'
				},
				{
					label: t('Risk Assessments'),
					itemLabel: t('risk assessment'),
					value: 'risk-assessments',
					params: savedFilters['risk-assessments']
						? savedFilters['risk-assessments']
						: 'exclude_retired=true&order_by=title&order=asc&search=',
					permission: 'risk_assessment_view'
				},
				{
					label: t('Custom Airspace'),
					itemLabel: t('custom airspace layer'),
					value: 'custom-airspace-layers',
					params: savedFilters['custom-airspace-layers']
						? savedFilters['custom-airspace-layers']
						: '&search=',
					permission: 'airspace_feature_view'
				}
			]
		},
		{
			categoryLabel: t('Admin'),
			items: [
				{
					label: t('Users'),
					itemLabel: t('user'),
					value: 'users',
					params: savedFilters.users
						? savedFilters.users
						: 'retired=false&order_by=lastname&order=asc&search=',
					permission: 'user_view'
				},
				{
					label: `${t('Roles')} & ${t('Permissions')}`,
					itemLabel: t('role'),
					value: 'roles',
					params: savedFilters['roles']
						? savedFilters['roles']
						: 'order_by=name&order=asc&appends=workflow&search=',
					permission: 'role_view'
				},
				{
					label: t('Tags'),
					itemLabel: t('tag group'),
					value: 'tag-groups',
					params: 'order_by=name&order=asc',
					permission: 'tag_view'
				},
				{
					label: t('Connections'),
					itemLabel: t('connection'),
					value: 'integration',
					params: '&search=',
					permission: 'integration_view'
				},
				{
					label: t('Alerts'),
					itemLabel: t('alert'),
					value: 'alerts',
					params: '',
					permission: 'alert_view'
				}
			]
		}
	];
};

export const reportsModulesList = () => {
	const localStorageSearchFilters = localStorage.getItem(SEARCH_FILTERS);

	let savedFilters = {};
	if (localStorageSearchFilters) {
		savedFilters = JSON.parse(localStorageSearchFilters);
	}

	return [
		{
			categoryLabel: null,
			items: [
				{
					label: t('Dashboard'),
					itemLabel: t('dashboard'),
					value: 'dashboard',
					params: savedFilters.reportsDashboard
						? savedFilters.reportsDashboard
						: `start=${startOfYear}&stop=${currentDay}`
				}
			]
		},
		{
			categoryLabel: t('Operations'),
			items: [
				{
					label: t('Missions'),
					itemLabel: t('missions'),
					value: 'missions',
					params: savedFilters.missionsReport
						? savedFilters.missionsReport
						: `start=${startOfYear}&stop=${currentDay}&columns_by=month&rows_by=pilot&total_by=count`
				},
				{
					label: t('Flights'),
					itemLabel: t('flights'),
					value: 'flights',
					params: savedFilters.flightsReport
						? savedFilters.flightsReport
						: `start=${startOfYear}&stop=${currentDay}&columns_by=month&rows_by=pilot&total_by=count`
				}
			]
		},
		{
			categoryLabel: t('Operators'),
			items: [
				{
					label: t('Compliance'),
					itemLabel: t('faa-compliance-v2'),
					value: 'faa-compliance-v2',
					params: savedFilters['faa-compliance-v2']
						? savedFilters['faa-compliance-v2']
						: 'expired-status=all&roles='
				},
				{
					label: t('Recency'),
					itemLabel: t('operator-recency-v2'),
					value: 'operator-recency-v2',
					params: savedFilters['operator-recency-v2']
						? savedFilters['operator-recency-v2']
						: 'threshold-hours=1&threshold-days=30&roles='
				}
			]
		},
		{
			categoryLabel: t('Assets'),
			items: [
				{
					label: t('Aircraft Registration'),
					itemLabel: t('aircraft-registration-v2'),
					value: 'aircraft-registration-v2',
					params: savedFilters['aircraft-registration-v2']
						? savedFilters['aircraft-registration-v2']
						: 'aircraft_model_name='
				}
			]
		}
	];
};
